import React, { useEffect, useMemo, useState } from "react";
import { Grid, Typography, Box } from "@mui/material";
import { SectionHeader } from "../../ui/SectionHeader";
import idIcon from "../../../assets/svgIcons/id.svg";

const ApplicantAdditionalDetails = ({
  fields,
  renderField,
  formikRef,
  values,
  nationalityOptions = [],
  dropdownData = {},
}: any) => {
  const formValues = values || formikRef?.current?.values || {};

  useEffect(() => {
    if (nationalityOptions.length > 0) {
      // Set default nationality to 'Indian' if not already set
      const nationalityField = fields.find(
        (f: any) => /nationality/i.test(f?.name || "") || /naionality/i.test(f?.name || ""),
      );
      if (nationalityField) {
        const fieldName = nationalityField.name;
        const currentValue = formikRef?.current?.values?.[fieldName];
        if (
          !currentValue ||
          (typeof currentValue === "object" &&
            !currentValue.value &&
            !currentValue.label)
        ) {
          const indiaOption = nationalityOptions.find(
            (opt:any) =>
              opt.label?.toLowerCase() === "indian" ||
              opt.value?.toLowerCase() === "indian" ||
              opt.value?.toLowerCase() === "in",
          );
          if (indiaOption) {
            formikRef?.current?.setFieldValue(fieldName, indiaOption, false);
          }
        }
      }
    }
  }, [fields, formikRef, nationalityOptions]);

  const enrichField = (field: any) => {
    const name = String(field?.name || "");
    if (/nationality/i.test(name) || /naionality/i.test(name)) {
      return { ...field, options: { options: nationalityOptions } };
    }
    if (name === "loanLoanTenure") {
      return { ...field, options: { options: dropdownData?.loanTenureOptions || [] } };
    }
    return field;
  };

  useEffect(() => {
    const sourceLoanAmount = formikRef?.current?.values?.loanAmount;
    const targetLoanAmount = formikRef?.current?.values?.loanLoanAmount;
    const isTargetLoanAmountEmpty =
      targetLoanAmount === undefined ||
      targetLoanAmount === null ||
      String(targetLoanAmount).trim() === "" ||
      Number(String(targetLoanAmount).replace(/,/g, "")) === 0;

    if (
      sourceLoanAmount !== undefined &&
      sourceLoanAmount !== null &&
      String(sourceLoanAmount).trim() !== "" &&
      isTargetLoanAmountEmpty
    ) {
      formikRef?.current?.setFieldValue("loanLoanAmount", sourceLoanAmount, false);
    }
  }, [formikRef?.current?.values?.loanAmount]);

  const sortedFields = useMemo(() => {
    if (!Array.isArray(fields)) return [];
    return [...fields].sort(
      (a: any, b: any) => (a?.fieldOrder ?? 0) - (b?.fieldOrder ?? 0),
    );
  }, [fields]);

  const applicantFields = useMemo(
    () =>
      sortedFields.filter((f: any) =>
        String(f?.name || "")
          .toLowerCase()
          .startsWith("applicant"),
      ),
    [sortedFields],
  );

  const loanFields = useMemo(
    () =>
      sortedFields.filter((f: any) =>
        String(f?.name || "")
          .toLowerCase()
          .startsWith("loan"),
      ),
    [sortedFields],
  );

  const isFilled = (value: any): boolean => {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "number") return true;
    if (typeof value === "boolean") return true;
    if (value && typeof value === "object") {
      if ("value" in value) return String(value.value ?? "").trim() !== "";
      return Object.keys(value).length > 0;
    }
    return String(value ?? "").trim() !== "";
  };

  const getCompletion = (sectionFields: any[]) => {
    const requiredFields = sectionFields.filter(
      (f: any) => f?.required !== false,
    );
    const filledCount = requiredFields.filter((field: any) =>
      isFilled(formValues?.[field?.name]),
    ).length;
    return `${filledCount} / ${requiredFields.length}`;
  };

  return (
    <>
      <Grid container mt={1} spacing={2} sx={{ px: { xs: 0, sm: 2 }, mb: 2 }}>
        <Grid item xs={12}>
          <Box sx={{ mb: 2, mt: 2 }}>
            <SectionHeader
              icon={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 40,
                    height: 40,
                    borderRadius: "8px",
                    bgcolor: "#e5feffff",
                  }}
                >
                  <img
                    src={idIcon}
                    alt="icon"
                    style={{
                      width: 28,
                      height: 28,
                    }}
                  />
                </Box>
              }
              title={"Applicant Details"}
              subtitle={"Applicant Related Details"}
              completion={getCompletion(applicantFields)}
            />
          </Box>
        </Grid>

    <Grid item xs={12} sx={{ px: "20px" }}>
      <Grid container spacing={2}>
          {applicantFields.map((field: any) => (
            <React.Fragment key={field?.id || field?.name}>
              {renderField?.(enrichField(field))}
            </React.Fragment>
          ))}
        </Grid>
    </Grid>

        <Grid item xs={12}>
          <Box sx={{ mb: 2, mt: 2 }}>
            <SectionHeader
              icon={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 40,
                    height: 40,
                    borderRadius: "8px",
                    bgcolor: "#e5feffff",
                  }}
                >
                  <img
                    src={idIcon}
                    alt="icon"
                    style={{
                      width: 28,
                      height: 28,
                    }}
                  />
                </Box>
              }
              title={"Loan Details"}
              subtitle={"Loan Related Details"}
              completion={getCompletion(loanFields)}
            />
          </Box>
        </Grid>

    <div className="loan-details-section" style={{ width: "100%", display: "flex", alignItems: "flex-end", gap: "16px", padding: "0px 20px" }}>
        {loanFields.map((field: any) => (
          <React.Fragment key={field?.id || field?.name}>
            {renderField?.(enrichField(field))}
          </React.Fragment>
        ))}
        </div>
      </Grid>
    </>
  );
};

export default ApplicantAdditionalDetails;
